/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
import type {
  Cache,
  CacheExchangeOpts,
  DataFields,
  FieldArgs,
} from "@urql/exchange-graphcache";

import { gql } from "urql";

import type { AuditFinding } from "./gql/graphql";

import { FindingStatus } from "./gql/graphql";

const claimFindingsStatusQuery = gql`
  query ClaimFindingsStatus($id: UUID) {
    claim(id: $id) {
      __typename
      id
      findings {
        __typename
        id
        status
        accepted
        needsReview
        latestReview {
          __typename
          authorId
          createdAt
          deleted
          rationale
        }
        findingItemizedBillData {
          __typename
          id
          ibLineId
        }
      }
    }
  }
`;

// Findings rendered in the table are `Finding` entities, but setFindingStatus
// returns `FindingV2` (a different __typename, so normalization won't touch
// them). Mirror the status onto the `Finding` entity so the grid + finding
// detail tables react instantly — both for the optimistic result and the real
// one (which also carries any server-side auto-declined findings).
const writeFindingStatus = (cache: Cache, finding: any) => {
  if (!finding?.id || !finding.status) return;
  cache.writeFragment(
    gql`
      fragment _SetFindingStatus on Finding {
        id
        status
        accepted
        needsReview
      }
    `,
    {
      __typename: "Finding",
      id: finding.id,
      status: finding.status,
      accepted: finding.status === "ACCEPTED",
      needsReview: finding.status === "SUGGESTED",
    },
  );
};

const invalidateClaimFindings = (cache: Cache, claimId: unknown) => {
  if (typeof claimId !== "string") return;
  cache
    .inspectFields({ __typename: "Claim", id: claimId })
    .filter((field) => field.fieldName === "findings")
    .forEach((field) =>
      cache.invalidate(
        { __typename: "Claim", id: claimId },
        "findings",
        field.arguments ?? undefined,
      ),
    );
};

const invalidateQueries = (
  operationName: string,
  cache: Cache,
  invalidates: { field: string; args: FieldArgs }[],
) =>
  invalidates.forEach((invalidate) => {
    console.debug(
      "%c%s: Invaliding query: %s %O",
      "color: chartreuse; font-weight: bold;",
      operationName,
      invalidate.field,
      invalidate.args,
    );
    cache.invalidate("Query", invalidate.field, invalidate.args);
  });

// Idea: introduce a function here to get the graphCache, that supports
// an 'observer' interface/arg that gets notified with read-only props
// when each member of keys/queries/mutations gets called, maybe at the end
// of each function, e.g. containing {request: {result, args, cache, info}, response}
// where request is the inputs, and response is optional, up to the writer (although
// we could introduce utils to wrap/return the values) to provide some detail on the
// update...  Or, we coule introduce classes, e.g. Query, Mutation, Key and internalize
// all updates wrapped with protected members that can be overridden to observe updates
// Purpose here would be primarily testing, but could also allow some interesting things,
// like the ability to colocate the cache function with the code it pertains to...
const graphCache: Partial<CacheExchangeOpts> = {
  keys: {
    ActiveStatistics: () => null,
    AgentMessageContent: () => null,
    AgentMessageUserValidation: () => null,
    AnnotationLabel: () => null,
    AssignClaimsToAuditGroupPayload: () => null,
    AskAutodorFileVectorsExistResponse: () => null,
    AskAutodorQueueSummary: () => null,
    AuditFindingAggregates: () => null,
    AuditFindingReviewsConnection: () => null,
    AuditFindingRuleType: (data) => {
      return "" + data.type;
    },
    AuditFindingSumAggregates: () => null,
    AuditFindingsConnection: () => null,
    AuditRule: () => null,
    AuditorUsersConnection: () => null,
    Autodor: () => null,
    AutodorFinding: (data) => (data.id as string) ?? null,
    AutodorMetadata: () => null,
    AutodorSink: () => null,
    BatchClaimAggregates: () => null,
    BatchClaimDocumentsConnection: () => null,
    BatchClaimLinesConnection: () => null,
    BatchClaimStateAggregates: () => null,
    BatchClaimStateSumAggregates: () => null,
    BatchClaimSumAggregates: () => null,
    BatchClaimsConnection: () => null,
    BlockerSegmentHistoryEntry: () => null,
    BucketClaimAggregates: () => null,
    BucketClaimsConnection: () => null,
    CaseAgentType: () => null,
    ClaimAuditTypeInfo: () => null,
    ClaimAuditGroupsConnection: () => null,
    ClaimAuditTypesResponse: () => null,
    ClaimIntakeResponse: () => null,
    CsvRow: (data) => "" + (data.alaRowId ?? data.headerRowId),
    CreateClaimAuditGroupPayload: () => null,
    DashboardSummary: () => null,
    FeatureFlagValue: (featureFlag) => featureFlag.key?.toString() ?? null,
    FileAnnotationCoordinates: () => null,
    FoxitCredentials: () => null,
    HistoricStatistics: () => null,
    HistoryTimelineEvents: () => null,
    InlineCitations: () => null,
    LatestReview: () => null,
    MUEAuditRule: () => null,
    MUEAuditRuleRationale: () => null,
    NonBillableAuditRule: () => null,
    NpiProvider: (data) => "" + data.npi,
    PTPAuditRule: () => null,
    PrepareFileForUploadResult: () => null,
    PromptFeedback: () => null,
    PromptResponsePage: () => null,
    PromptResponseRating: () => null,
    ProviderDashboardSummary: () => null,
    ProviderUserFacilitiesConnection: () => null,
    ReportAggregate: () => null,
    SignedDownloadUrl: () => null,
    StageTrackerBlockerSegment: () => null,
    SupportingDocumentation: () => null,
    SsoDomainIdentityProvider: (data) => String(data.id),
    SsoDomainIdentityProviderPayload: () => null,
    SsoDomainIdentityProvidersConnection: () => null,
    GQLAuditRule: (data) => String(data.id),
    GQLAuditRuleRunResult: (data) => String(data.id),
    AuditTestResult: (data) => String(data.id),
    AuditTestResultLineItem: () => null,
    AuditTestResultHumanFinding: () => null,
    AuditTestResultRuleFinding: () => null,
    AuditTestResultsConnection: () => null,
    AuditTestResultsEdge: () => null,
    FindingField: () => null,
    GQLAuditRuleFlaggedItem: (data) => String(data.id),
    GQLAuditRuleGeneratedFinding: (data) => String(data.id),
    GQLAuditRuleTestSummary: () => null,
    GQLAuditRuleFieldAccuracy: () => null,
    GQLAuditRuleFieldAccuracyMetric: () => null,
    UserCognitoSettings: () => null,
    UpdateClaimAuditGroupPayload: () => null,
    ClaimsSummary: () => null,
    PhaseSummary: () => null,
  },
  resolvers: {
    Query: {
      auditorUser: (_parent, args) => ({
        __typename: "AuditorUser",
        id: args.id,
      }),
      auditorBucket: (_parent, args) => ({
        __typename: "AuditorBucket",
        id: args.id,
      }),
      auditFindingReview: (_parent, args) => ({
        __typename: "AuditFindingReview",
        id: args.id,
      }),
      auditFinding: (_parent, args) => ({
        __typename: "AuditFinding",
        id: args.id,
      }),
      finding: (_parent, args) => ({
        __typename: "Finding",
        id: args.id,
      }),
      batchClaimLine: (_parent, args) => ({
        __typename: "BatchClaimLine",
        id: args.id,
      }),
      batchClaim: (_parent, args) => ({
        __typename: "BatchClaim",
        id: args.id,
      }),
      bucketClaim: (_parent, args) => ({
        __typename: "BucketClaim",
        id: args.id,
      }),
      auditFindingReport: (_parent, args) => ({
        __typename: "AuditFindingReport",
        id: args.id,
      }),
      auditRule: (_parent, args) =>
        args.id ? { __typename: "AuditRule" as const, id: args.id } : undefined,
      auditRuleV2: (_parent, args) =>
        args.id
          ? { __typename: "GQLAuditRule" as const, id: args.id }
          : undefined,
    },
  },
  optimistic: {
    // Flip the selected findings to the requested status immediately so the
    // table reflects the bulk Accept/Decline before the round-trip resolves.
    // Auto-declined findings aren't known until the server responds, so they
    // are filled in by the `updates` handler with the real result.
    setFindingStatus: (args: any) => {
      const input = args?.input ?? {};
      const findingIds: string[] = input.findingIds ?? [];
      const status: string = input.status;
      return {
        __typename: "SetFindingStatusPayload",
        findings: findingIds.map((id) => ({
          __typename: "FindingV2",
          id,
          status,
        })),
        autoDeclinedFindings: [],
      };
    },
  },
  updates: {
    Mutation: {
      setFindingStatus: (result: DataFields, _args, cache, info) => {
        if (info.error) return;
        const payload = result?.setFindingStatus as any;
        if (!payload) return;
        for (const finding of payload.findings ?? []) {
          writeFindingStatus(cache, finding);
        }
        for (const finding of payload.autoDeclinedFindings ?? []) {
          writeFindingStatus(cache, finding);
        }
      },
      createIbLineFindings: (result, _args, cache, info) => {
        if (info.error) {
          return;
        }
        const claimId = (result?.createAuditFindings as any).auditFindings[0]
          ?.batchClaimId;
        if (claimId) {
          const claimArgs = { id: claimId };

          invalidateQueries(info.fieldName, cache, [
            { field: "batchClaim", args: claimArgs },
          ]);
          invalidateClaimFindings(cache, claimId);

          return;
        }
      },
      createUbClaimLineFindings: (result, _args, cache, info) => {
        if (info.error) {
          return;
        }
        const claimId = (result?.createAuditFindings as any).auditFindings[0]
          ?.batchClaimId;
        if (claimId) {
          const claimArgs = { id: claimId };

          invalidateQueries(info.fieldName, cache, [
            { field: "batchClaim", args: claimArgs },
          ]);
          invalidateClaimFindings(cache, claimId);

          return;
        }
      },
      createReviewedAuditFinding: (result: DataFields, _args, cache, info) => {
        if (info.error) {
          return;
        }
        const reviewedAuditFinding = (result?.createReviewedAuditFinding as any)
          ?.reviewedAuditFinding;
        const auditFinding = reviewedAuditFinding?.auditFinding;
        if (!reviewedAuditFinding || !auditFinding) {
          return;
        }

        const claimId = auditFinding.batchClaimId;
        const targetFindingId = auditFinding.id;
        const ibLineId =
          auditFinding.batchClaimLineItemAudit?.batchClaimLineItem?.ibLineId;
        const accepted = reviewedAuditFinding.accepted;

        if (!claimId || !targetFindingId) {
          return;
        }

        const newStatus = accepted
          ? FindingStatus.ACCEPTED
          : FindingStatus.DECLINED;

        const latestReview = reviewedAuditFinding.authorId
          ? {
              __typename: "LatestReview" as const,
              authorId: reviewedAuditFinding.authorId,
              createdAt: reviewedAuditFinding.createdAt,
              deleted: reviewedAuditFinding.deleted ?? false,
              rationale: reviewedAuditFinding.rationale ?? null,
            }
          : undefined;

        cache.updateQuery(
          { query: claimFindingsStatusQuery, variables: { id: claimId } },
          (data: any) => {
            if (!data?.claim) {
              return data;
            }

            const updatedFindings = data.claim.findings.map((f: any) => {
              if (f.id === targetFindingId) {
                return {
                  ...f,
                  status: newStatus,
                  accepted,
                  needsReview: false,
                  ...(latestReview && { latestReview }),
                };
              }

              if (
                accepted &&
                ibLineId &&
                f.findingItemizedBillData?.ibLineId === ibLineId &&
                f.status === FindingStatus.ACCEPTED
              ) {
                return {
                  ...f,
                  status: FindingStatus.DECLINED,
                  accepted: false,
                  needsReview: false,
                };
              }

              return f;
            });

            return {
              ...data,
              claim: {
                ...data.claim,
                findings: updatedFindings,
              },
            };
          },
        );

        const claimArgs = { id: claimId };

        invalidateQueries(info.fieldName, cache, [
          { field: "batchClaim", args: claimArgs },
        ]);
        invalidateClaimFindings(cache, claimId);
      },
      createClaimAuditGroup: (_result: DataFields, _args, cache, info) => {
        if (info.error) {
          return;
        }

        invalidateQueries(info.fieldName, cache, [
          { field: "claimAuditGroups", args: {} },
        ]);
      },
      updateClaimAuditGroup: (_result: DataFields, _args, cache, info) => {
        if (info.error) {
          return;
        }

        invalidateQueries(info.fieldName, cache, [
          { field: "claimAuditGroups", args: {} },
        ]);
      },
      versionAuditFindings: (result: DataFields, _args, cache, info) => {
        if (info.error) {
          return;
        }
        const claimIds: string[] = (
          (result?.versionAuditFindings as any).auditFindings ?? []
        )
          .map((af: AuditFinding) => af.batchClaimId)
          .filter((id: string) => !!id);

        if (claimIds.length > 0 && claimIds[0]) {
          const claimArgs = { id: claimIds[0] };

          invalidateQueries(info.fieldName, cache, [
            { field: "batchClaim", args: claimArgs },
          ]);
          invalidateClaimFindings(cache, claimIds[0]);
        }
      },
    },
  },
};

export { graphCache };
